import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { animalService, Animal } from '../services/animalService';
import { transferService } from '../services/transferService';
import { uploadService } from '../services/uploadService';
import { SearchInput, SearchIcon } from '../components/SearchInput';
import { CpfInput } from '../components/CpfInput';
import './Animals.css';

const Animals: React.FC = () => {
  const { user } = useAuth();
  const canAddAnimal = user && user.role !== 'comum';
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchType, setSearchType] = useState<'cpf' | 'microchip'>('microchip');
  const [searchValue, setSearchValue] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailAnimal, setDetailAnimal] = useState<Animal | null>(null);
  const [selectedAnimal, setSelectedAnimal] = useState<Animal | null>(null);
  const [transferData, setTransferData] = useState({
    to_cpf: '',
    to_responsible_cpf: '',
    reason: '',
  });
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    loadAnimals();
  }, []);

  const loadAnimals = async () => {
    try {
      setLoading(true);
      const data = await animalService.getAnimals();
      setAnimals(data);
    } catch (error: any) {
      console.error('Erro ao carregar animais:', error);
      if (error.response) {
        if (error.response.status === 401) {
          // Token inválido - será tratado pelo interceptor
          return;
        }
        alert(error.response.data?.error || 'Erro ao carregar animais');
      } else if (error.request) {
        alert('Erro ao conectar com o servidor. Verifique sua conexão.');
      } else {
        alert('Erro ao carregar animais. Tente novamente.');
      }
      setAnimals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      loadAnimals();
      return;
    }

    try {
      let data: Animal[];
      if (searchType === 'cpf') {
        data = await animalService.searchByCPF(searchValue);
      } else {
        data = await animalService.searchByMicrochip(searchValue);
      }
      setAnimals(data);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao buscar animais');
    }
  };

  const handleMarkDeceased = async (id: number) => {
    if (!window.confirm('Deseja marcar este animal como falecido? Ele continuará no sistema na lista de animais falecidos.')) {
      return;
    }

    try {
      await animalService.markDeceased(id);
      alert('Animal marcado como falecido.');
      loadAnimals();
    } catch (error: any) {
      console.error('Erro ao marcar animal como falecido:', error);
      alert(error.response?.data?.error || 'Erro ao marcar como falecido. Tente novamente.');
    }
  };

  const handleTransferClick = (animal: Animal) => {
    setSelectedAnimal(animal);
    setTransferData({
      to_cpf: '',
      to_responsible_cpf: '',
      reason: '',
    });
    setShowTransferModal(true);
  };

  const handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAnimal) return;

    setTransferring(true);
    try {
      await transferService.createTransfer({
        animal_id: selectedAnimal.id,
        to_cpf: transferData.to_cpf,
        to_responsible_cpf: transferData.to_responsible_cpf || transferData.to_cpf,
        reason: transferData.reason,
      });
      alert('Transferência solicitada com sucesso!');
      setShowTransferModal(false);
      setSelectedAnimal(null);
      setTransferData({
        to_cpf: '',
        to_responsible_cpf: '',
        reason: '',
      });
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao criar transferência');
    } finally {
      setTransferring(false);
    }
  };

  const handleViewDetails = (animal: Animal) => {
    setDetailAnimal(animal);
    setShowDetailModal(true);
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>Animais Cadastrados</h1>
        {canAddAnimal && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <Link to="/animals/new" className="btn btn-primary">
              Novo Animal
            </Link>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Buscar Animais</h2>
        <div className="search-form">
          <select
            value={searchType}
            onChange={(e) => {
              setSearchType(e.target.value as 'cpf' | 'microchip');
              setSearchValue('');
            }}
            className="form-control search-type-select"
          >
            <option value="microchip">Número do Microchip</option>
            <option value="cpf">CPF do Responsável</option>
          </select>
          {searchType === 'cpf' ? (
            <div className="search-input-wrapper" style={{ flex: 1, minWidth: 200 }}>
              <span className="search-input-icon" aria-hidden="true">
                <SearchIcon size={18} />
              </span>
              <CpfInput
                value={searchValue}
                onChange={(formatted) => setSearchValue(formatted)}
                placeholder="Digite o CPF (000.000.000-00)"
                className="form-control search-input"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
          ) : (
            <SearchInput
              placeholder="Digite o número do microchip"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="search-input-flex"
            />
          )}
          <button onClick={handleSearch} className="btn btn-primary">
            Buscar
          </button>
          <button 
            onClick={() => {
              setSearchValue('');
              loadAnimals();
            }} 
            className="btn btn-secondary"
          >
            Limpar
          </button>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Espécie</th>
              <th>Raça</th>
              <th>Microchip</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {animals.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center' }}>
                  Nenhum animal encontrado
                </td>
              </tr>
            ) : (
              animals.map((animal) => (
                <tr key={animal.id}>
                  <td>{animal.name}</td>
                  <td>{animal.species}</td>
                  <td>{animal.breed || '-'}</td>
                  <td>{animal.microchip?.number || animal.microchip_id || '-'}</td>
                  <td>
                    <button
                      onClick={() => handleViewDetails(animal)}
                      className="btn btn-view-details"
                      title="Ver todos os dados"
                      style={{ marginRight: '8px', marginBottom: '5px' }}
                    >
                      <SearchIcon size={16} />
                    </button>
                    {animal.can_edit !== false && (
                      <>
                        <Link
                          to={`/animals/${animal.id}`}
                          className="btn btn-primary"
                          style={{ marginRight: '10px', marginBottom: '5px' }}
                        >
                          Editar
                        </Link>
                        <Link
                          to={`/missing-animals/report?animal_id=${animal.id}`}
                          className="btn btn-warning"
                          style={{ marginRight: '10px', marginBottom: '5px' }}
                          title="Reportar como desaparecido"
                        >
                          🚨 Alerta
                        </Link>
                        <button
                          onClick={() => handleTransferClick(animal)}
                          className="btn btn-success"
                          style={{ marginRight: '10px', marginBottom: '5px' }}
                        >
                          Transferir
                        </button>
                        <button
                          onClick={() => handleMarkDeceased(animal.id)}
                          className="btn btn-danger"
                          style={{ marginBottom: '5px' }}
                          title="Marcar como falecido"
                        >
                          Falecido
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showDetailModal && detailAnimal && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content modal-detail modal-id-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header id-card-header">
              <span className="id-card-title">Carteira de Identificação</span>
              <button
                className="modal-close"
                onClick={() => setShowDetailModal(false)}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>
            <div className="modal-body id-card-body">
              <div className="id-card">
                <div className="id-card-inner">
                  <div className="id-card-photo-wrap">
                    {detailAnimal.photo_url ? (
                      <img
                        src={uploadService.getImageUrl(detailAnimal.photo_url)}
                        alt={detailAnimal.name}
                        className="id-card-photo"
                      />
                    ) : (
                      <div className="id-card-photo-placeholder">
                        <span>Sem foto</span>
                      </div>
                    )}
                  </div>
                  <div className="id-card-fields">
                    <div className="id-card-row id-card-name">{detailAnimal.name}</div>
                    <div className="id-card-row">
                      <span className="id-card-label">Espécie</span>
                      <span className="id-card-value">{detailAnimal.species}</span>
                    </div>
                    <div className="id-card-row">
                      <span className="id-card-label">Raça</span>
                      <span className="id-card-value">{detailAnimal.breed || '—'}</span>
                    </div>
                    <div className="id-card-row">
                      <span className="id-card-label">Sexo</span>
                      <span className="id-card-value">{detailAnimal.gender || '—'}</span>
                    </div>
                    <div className="id-card-row">
                      <span className="id-card-label">Nascimento</span>
                      <span className="id-card-value">
                        {detailAnimal.birth_date ? new Date(detailAnimal.birth_date).toLocaleDateString('pt-BR') : '—'}
                      </span>
                    </div>
                    <div className="id-card-row">
                      <span className="id-card-label">Cor</span>
                      <span className="id-card-value">{detailAnimal.color || '—'}</span>
                    </div>
                    {detailAnimal.microchip && (
                      <div className="id-card-row id-card-microchip">
                        <span className="id-card-label">Microchip</span>
                        <span className="id-card-value id-card-microchip-number">{detailAnimal.microchip.number}</span>
                      </div>
                    )}
                  </div>
                </div>
                {detailAnimal.description && (
                  <div className="id-card-description">
                    <span className="id-card-label">Descrição</span>
                    <p>{detailAnimal.description}</p>
                  </div>
                )}
                {detailAnimal.responsible && (
                  <div className="id-card-section">
                    <div className="id-card-section-title">Responsável</div>
                    <div className="id-card-grid">
                      <div className="id-card-row"><span className="id-card-label">Nome</span><span className="id-card-value">{detailAnimal.responsible.name}</span></div>
                      <div className="id-card-row"><span className="id-card-label">CPF</span><span className="id-card-value">{detailAnimal.responsible.cpf}</span></div>
                      <div className="id-card-row"><span className="id-card-label">Telefone</span><span className="id-card-value">{detailAnimal.responsible.phone || '—'}</span></div>
                      <div className="id-card-row"><span className="id-card-label">E-mail</span><span className="id-card-value">{detailAnimal.responsible.email || '—'}</span></div>
                      <div className="id-card-row id-card-full"><span className="id-card-label">Endereço</span><span className="id-card-value">{detailAnimal.responsible.address || '—'}</span></div>
                      <div className="id-card-row"><span className="id-card-label">Cidade/UF</span><span className="id-card-value">{[detailAnimal.responsible.city, detailAnimal.responsible.state].filter(Boolean).join(' / ') || '—'}</span></div>
                      <div className="id-card-row"><span className="id-card-label">CEP</span><span className="id-card-value">{detailAnimal.responsible.zip_code || '—'}</span></div>
                    </div>
                  </div>
                )}
                {detailAnimal.microchip && (detailAnimal.microchip.manufacturer || detailAnimal.microchip.implant_date || detailAnimal.microchip.veterinarian_name) && (
                  <div className="id-card-section">
                    <div className="id-card-section-title">Dados do microchip</div>
                    <div className="id-card-grid">
                      {detailAnimal.microchip.manufacturer && <div className="id-card-row"><span className="id-card-label">Fabricante</span><span className="id-card-value">{detailAnimal.microchip.manufacturer}</span></div>}
                      {detailAnimal.microchip.implant_date && <div className="id-card-row"><span className="id-card-label">Implantação</span><span className="id-card-value">{new Date(detailAnimal.microchip.implant_date).toLocaleDateString('pt-BR')}</span></div>}
                      {detailAnimal.microchip.implant_location && <div className="id-card-row"><span className="id-card-label">Local</span><span className="id-card-value">{detailAnimal.microchip.implant_location}</span></div>}
                      {detailAnimal.microchip.veterinarian_name && <div className="id-card-row"><span className="id-card-label">Veterinário</span><span className="id-card-value">{detailAnimal.microchip.veterinarian_name}{detailAnimal.microchip.veterinarian_crmv ? ` (CRMV ${detailAnimal.microchip.veterinarian_crmv})` : ''}</span></div>}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                {detailAnimal.can_edit !== false && (
                  <Link
                    to={`/animals/${detailAnimal.id}`}
                    className="btn btn-primary"
                    onClick={() => setShowDetailModal(false)}
                  >
                    Editar Animal
                  </Link>
                )}
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="btn btn-secondary"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTransferModal && selectedAnimal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Transferir Animal</h2>
              <button
                className="modal-close"
                onClick={() => setShowTransferModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                <strong>Animal:</strong> {selectedAnimal.name} ({selectedAnimal.species})
              </p>
              <form onSubmit={handleTransferSubmit}>
                <div className="form-group">
                  <label>CPF do Destinatário (Usuário) *</label>
                  <CpfInput
                    value={transferData.to_cpf}
                    onChange={(formatted) =>
                      setTransferData({
                        ...transferData,
                        to_cpf: formatted,
                        to_responsible_cpf: transferData.to_responsible_cpf || formatted,
                      })
                    }
                    required
                    className="form-control"
                  />
                  <small>O usuário deve estar cadastrado no sistema</small>
                </div>
                <div className="form-group">
                  <label>CPF do Responsável *</label>
                  <CpfInput
                    value={transferData.to_responsible_cpf}
                    onChange={(formatted) => setTransferData({ ...transferData, to_responsible_cpf: formatted })}
                    required
                    className="form-control"
                  />
                  <small>Pode ser o mesmo CPF do usuário</small>
                </div>
                <div className="form-group">
                  <label>Motivo da Transferência (Opcional)</label>
                  <textarea
                    value={transferData.reason}
                    onChange={(e) =>
                      setTransferData({ ...transferData, reason: e.target.value })
                    }
                    rows={3}
                    className="form-control"
                    placeholder="Descreva o motivo da transferência..."
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => setShowTransferModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={transferring}
                  >
                    {transferring ? 'Enviando...' : 'Solicitar Transferência'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Animals;
